import { spacing } from "globalStyles";
import { Section } from "interfaces/content";
import { useAppContext } from "providers/App";
import React, { ComponentType, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { breakpoints } from "themes/breakpoints";
import AllInformations from "./AllInformations";
import Banner from "./Banner";
import ContactUs from "./ContactUs";
import Footer from "./Footer";
import Menu from "./Menu";
import Newsletter from "./Newsletter";
import PermitsManagement from "./PermitsManagement";
import StreetSection from "./Street";
import { setMetaDescription } from "utils/dom";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SectionsObject: Record<Partial<Section["type"]>, ComponentType<any>> = {
  hero: Banner,
  menu: Menu,
  street: StreetSection,
  permitsManagement: PermitsManagement,
  allInformations: AllInformations,
  contactUs: ContactUs,
  newsletter: Newsletter,
  footer: Footer,
};

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { place } = useParams();
  const { handleSetContent, content, setIsLoading } = useAppContext();

  useEffect(() => {
    const handleContent = async () => {
      setIsLoading(true);

      await fetch(`${process.env.REACT_APP_BASE_RELATIVE_ROUTES}/content/${place}.json`)
        .then((res) => res.json())
        .then((data) => handleSetContent(data))
        .catch((error) => {
          navigate("/");
          console.error("Error fetching the JSON file:", error);
        })
        .finally(() => setIsLoading(false));
    };

    handleContent();
  }, [place, handleSetContent, setIsLoading, navigate]);

  useEffect(() => {
    if (content?.name) {
      setMetaDescription(`DataRede - ${content.name}`);
    }
  }, [content?.name]);

  const sections = useMemo(() => {
    if (!content || !content.sections.length) return null;

    return content.sections.map((section: Section) => {
      const Component = SectionsObject[section.type];
      return <Component key={section.data.id} data={section.data} />;
    });
  }, [content]);

  if (!sections) return null;

  return <Container>{sections}</Container>;
};

export default LandingPage;

const Container = styled.div`
  .content-container {
    width: 800px;
    padding: 70px 100px;
    margin: 0 auto;

    @media (max-width: ${breakpoints.lg}) {
      width: 80%;
      padding: 50px 10%;
    }

    @media (max-width: ${breakpoints.md}) {
      width: calc(100% - 40px);
      padding: 35px 20px;
    }

    p {
      margin: ${spacing.extraSmall}px;
    }

    h2 {
      @media (max-width: ${breakpoints.md}) {
        font-size: 20px;
      }
    }
  }

  .section-title {
    margin-bottom: ${spacing.extraLarge}px;

    @media (max-width: ${breakpoints.md}) {
      margin-bottom: 10px;
    }
  }
`;
